import React, { useState, useRef, useEffect } from 'react';
import styles from './CustomSelect.module.scss';
import { ChevronDown, Search } from 'lucide-react';
import CheckIcon from '../../../assets/iconsTsx/CheckIcon.tsx';

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string; description?: string; variant?: 'default' | 'accent' }[];
  placeholder?: string;
  icon?: React.ReactNode;
  label?: string;
  error?: string;
  placement?: 'bottom' | 'top';
  searchable?: boolean;
  searchPlaceholder?: string;
}

const removeAccents = (str: string) =>
  str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Seleccionar',
  icon,
  label,
  error,
  placement = 'bottom',
  searchable = false,
  searchPlaceholder = 'Buscar...'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchable) {
      setSearchTerm('');
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isOpen, searchable]);

  const selectedOption = options.find(opt => opt.value === value);
  // En varios usos el "placeholder" se representa como una opción con `value: ""`.
  // En ese caso queremos que se vea como placeholder (gris) y no como selección real.
  const isPlaceholder = !selectedOption || value === "" || selectedOption.value === "";

  const filteredOptions = searchable && searchTerm.trim()
    ? options.filter(opt => {
        const normalizedLabel = removeAccents(opt.label.toLowerCase());
        const normalizedSearch = removeAccents(searchTerm.toLowerCase());
        return normalizedLabel.includes(normalizedSearch);
      })
    : options;

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  const dropdownClassName = `${styles.dropdown} ${placement === 'top' ? styles.dropdownTop : ''} ${searchable ? styles.dropdownSearchable : ''}`;

  return (
    <div className={styles.container} ref={dropdownRef}>
      {label && (
        <label className={styles.label}>
          {icon && <span className={styles.labelIcon}>{icon}</span>}
          {label}
        </label>
      )}
      
      <div
        className={`${styles.select} ${isOpen ? styles.open : ''} ${error ? styles.error : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className={isPlaceholder ? styles.placeholderValue : styles.selectedValue}>
          {selectedOption ? selectedOption.label : placeholder}
        </div>
        <ChevronDown 
          className={`${styles.chevron} ${isOpen ? styles.rotated : ''}`} 
          size={18} 
        />
      </div>
      {error && <span className={styles.errorText}>{error}</span>}

      {isOpen && (
        <div className={dropdownClassName}>
          {searchable && (
            <div className={styles.searchWrapper} onClick={(e) => e.stopPropagation()}>
              <Search size={20} className={styles.searchIcon} />
              <input
                ref={searchInputRef}
                type="text"
                className={styles.searchInput}
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          )}
          <div className={`${styles.optionsList} ${searchable ? styles.optionsListSearchable : ''}`}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={`${styles.option} ${option.variant === 'accent' ? styles.accent : ''} ${
                    value === option.value ? styles.selected : ''
                  }`}
                  onClick={() => handleSelect(option.value)}
                >
                  <div className={styles.optionContent}>
                    <span className={styles.optionLabel}>{option.label}</span>
                    {option.description && <span className={styles.optionDescription}>{option.description}</span>}
                  </div>
                  {value === option.value && (
                    <div className={styles.checkmark}>
                      <CheckIcon className={styles.checkIcon} />
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className={styles.noResults}>No hay resultados</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
